import axiosInstance from '@/lib/axios';
import { authApi } from '@/features/auth/services/auth.service';
import { localStorageService } from './localStorage.service';
import toast from 'react-hot-toast';

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });

  failedQueue = [];
};

export const setupAxiosInterceptors = () => {
  console.log('🔧 Setting up Axios interceptors for token refresh');


  // Request interceptor to add auth token and remote access log ID
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorageService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add remote access log ID if present
      const isRemoteAccess = localStorage.getItem('isRemoteAccess');
      const logId = localStorage.getItem('remoteAccessLogId');
      if (isRemoteAccess === 'true' && logId) {
        config.headers['x-remote-access-log-id'] = logId;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle token refresh
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message || 'Something went wrong';

      // Check if this is a remote access session FIRST
      const isRemoteAccess = localStorage.getItem('isRemoteAccess');

      if (status === 423) {
        console.log('🔒 Account is locked')
        localStorageService.clearAll()
        window.location.href = `/login?message=${encodeURIComponent(message)}`;
        return Promise.reject(error);
      }

      if (status === 401 && originalRequest.url.includes('refresh-token')) {
        console.log('🔄 401 error on refresh token endpoint');
        localStorageService.clearAll();
        window.location.href = `/login?message=${encodeURIComponent(message || 'Session expired. Please login again.')}`;
        return Promise.reject(error);
      }

      // Specific handle for "User not found" which often returns 404 or 401
      if ((status === 404 || status === 401) && (message.toLowerCase().includes('user not found') || message.toLowerCase().includes('deactivated'))) {
        console.log('❌ User not found or deactivated');
        localStorageService.clearAll();
        window.location.href = `/login?message=${encodeURIComponent(message)}`;
        return Promise.reject(error);
      }

      // If remote access and 401, immediately expire (no refresh token for remote access)
      if (status === 401 && isRemoteAccess === 'true') {
        console.log('❌ Remote access session expired');
        localStorageService.clearAll();
        window.location.href = `/login?message=${encodeURIComponent('Remote access session expired')}`;
        return Promise.reject(error);
      }
      // Don't show toast for 401 errors that will trigger refresh
      if (status !== 401 || originalRequest._retry) {
        
        toast.error(message);
      }

      if (status === 401 && !originalRequest._retry) {
        console.log('🔄 401 detected, attempting token refresh for:', originalRequest.url);

        if (isRefreshing) {
          console.log('⏳ Already refreshing, queuing request');
          // If we're already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          }).catch((err) => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Check if refresh token exists in cookies before calling API
          if (!document.cookie.includes('refreshToken')) {
            console.log('❌ No refresh token found in cookies, skipping API call');
            throw {
              response: {
                data: {
                  message: 'Session expired. Please login again.'
                }
              }
            };
          }

          console.log('🔄 Calling refresh token API...');
          const response = await authApi.refreshToken();
          if (!response.success) {
            localStorageService.removeUser()
          }
          const newAccessToken = response.data.accessToken;
          console.log('✅ Refresh token successful, new token received');

          // Update stored token
          localStorageService.storeToken(newAccessToken);

          // Update default Authorization header
          axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

          // Process the queue of failed requests
          processQueue(null, newAccessToken);
          console.log('🔄 Retrying original request with new token');

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);

        } catch (refreshError: any) {
          console.error('❌ Refresh token failed:', refreshError);
          processQueue(refreshError, null);

          // Refresh failed - redirect to login
          localStorageService.clearAll();
          const refreshMessage = refreshError.response?.data?.message || 'Session expired. Please login again.';
          console.log('🚪 Redirecting to login page');
          window.location.href = `/login?message=${encodeURIComponent(refreshMessage)}`;

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

// Initialize interceptors when this module is imported
setupAxiosInterceptors();